import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation } from '@tanstack/react-query';

import type { InFieldUser } from '@cognite/apm-client';

import { USER_INSTANCE_SPACE } from '../../../../constants/user-data-model';

import { UseRegisterCurrentUserContext } from './cdm-use-register-user-mutation.context';

/**
 * Registers the current user and links to its user preferences.
 *
 * @remarks
 * This hook should only be used at the provider level (inside useCurrentUserQuery).
 * It does not invalidate the user query, so using it elsewhere may cause stale data issues.
 */

export const useRegisterCurrentUser = () => {
  const { useAuthContext, makeToast, captureException, useUserServices } =
    useContext(UseRegisterCurrentUserContext);

  const { t } = useTranslation('activity');
  const { authState } = useAuthContext();

  const { userService, userPreferencesService } = useUserServices();

  const mutation = useMutation<InFieldUser, Error>({
    mutationFn: async () => {
      const userId = authState.user.cdfId;
      if (!userId) {
        throw new Error('User profile not found');
      }

      if (!userPreferencesService || !userService) {
        throw new Error('User services are not available');
      }

      await userPreferencesService.upsert([
        {
          externalId: userId,
        },
      ]);

      // Email and name are empty because they will be populated by the backend
      // from the user's CDF profile information, which is needed after a audit
      // identifying that all users had access to change all other user data in
      // the user system data model.
      const response = await userService.upsert([
        {
          externalId: userId,
          email: '',
          name: '',
          preferences: {
            externalId: userId,
            space: USER_INSTANCE_SPACE,
          },
        },
      ]);

      const user = response?.data?.items?.[0];
      if (!user) {
        throw new Error('Failed to register user');
      }
      return user;
    },
    onError: (err) => {
      makeToast({
        type: 'danger',
        body: t('failedToUpdateUser', 'Failed to update user data'),
      });
      captureException(err, {
        level: 'fatal',
        tags: {
          mutationKey: 'use-register-current-user',
        },
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isError: mutation.isError,
    error: mutation.error,
  };
};
